import { BigNumber } from '@ethersproject/bignumber'
import { useEffect, useState } from 'react'
import { EXECUTE_SELL_GAS_LIMIT, SELL_GAS_LIMIT, toToken } from '../../constants'
import {
  useApprovalForDelay,
  useBalance,
  useComparator,
  useCurrencySelect,
  useDebouncePair,
  useEthers,
  useNumberInput,
  usePair,
} from '../../hooks'
import { CurrencyValue, formatCurrency } from '../../model'
import { useApiTokenPrices, useGasPrice, useSettings } from '../../providers'
import { BN_10_18, getAffordableMaxValueFor, getCurrencyValues } from '../../utils'

export enum SwapState {
  WALLET_NOT_CONNECTED_SHOW_OUTPUT = 'WALLET_NOT_CONNECTED_SHOW_OUTPUT',
  WALLET_NOT_CONNECTED_HIDE_OUTPUT = 'WALLET_NOT_CONNECTED_HIDE_OUTPUT',
  TOKEN_NOT_SELECTED = 'TOKEN_NOT_SELECTED',
  MISSING_INPUT = 'MISSING_INPUT',
  LOADING = 'LOADING',
  PAIR_DOES_NOT_EXIST = 'PAIR_DOES_NOT_EXIST',
  INSUFFICIENT_FIRST_BALANCE = 'INSUFFICIENT_FIRST_BALANCE',
  NEED_FIRST_APPROVAL = 'NEED_FIRST_APPROVAL',
  INSUFFICIENT_SECOND_RESERVE = 'INSUFFICIENT_SECOND_RESERVE',
  PRICE_DEVIATION_LIMIT = 'PRICE_DEVIATION_LIMIT',
  TOKEN0_ABSOLUTE_LIMIT = 'TOKEN0_ABSOLUTE_LIMIT',
  TOKEN0_RELATIVE_LIMIT = 'TOKEN0_RELATIVE_LIMIT',
  TOKEN1_ABSOLUTE_LIMIT = 'TOKEN1_ABSOLUTE_LIMIT',
  TOKEN1_RELATIVE_LIMIT = 'TOKEN1_RELATIVE_LIMIT',
  NO_ISSUE = 'NO_ISSUE',
  EMPTY = 'EMPTY',
}

const STATES_TO_SHOW_OUTPUT = [
  SwapState.MISSING_INPUT,
  SwapState.NEED_FIRST_APPROVAL,
  SwapState.NO_ISSUE,
  SwapState.INSUFFICIENT_FIRST_BALANCE,
  SwapState.WALLET_NOT_CONNECTED_SHOW_OUTPUT,
]

const getAffordableMaxValue = getAffordableMaxValueFor(EXECUTE_SELL_GAS_LIMIT, SELL_GAS_LIMIT)
const INSUFFICIENT_ETH_MSG = 'Insufficient ETH balance for gas prepay and input. Please add more ETH to your wallet.'

export function useSwap() {
  const { account } = useEthers()
  const currencies = useCurrencySelect()
  const [controlled, setControlled] = useState<'first' | 'second'>('first')
  const [firstValue, setFirstValue] = useNumberInput(currencies.first?.decimals)
  const [secondValue, setSecondValue] = useNumberInput(currencies.second?.decimals)
  const { pair, exists } = usePair(currencies.first, currencies.second)
  const firstBalance = useBalance(currencies.first)
  const secondBalance = useBalance(currencies.second)
  const firstApproval = useApprovalForDelay(currencies.first)
  const secondApproval = useApprovalForDelay(currencies.second)
  const apiTokenPrices = useApiTokenPrices()
  const gasPrice = useGasPrice()
  const { settings } = useSettings()

  function onFirstChange(value: string) {
    if (gasPrice) {
      gasPrice.setMaxGasFeeForEthMax(undefined)
    }
    _onFirstChange(value)
  }

  function _onFirstChange(value: string) {
    setFirstValue(value)
    setControlled('first')
  }

  function onSecondChange(value: string) {
    setSecondValue(value)
    setControlled('second')
  }

  function setMax() {
    // Second value will be automatically recomputed
    if (firstBalance) {
      if (firstBalance.currency.isNative()) {
        if (gasPrice && gasPrice.delayGasPrice && gasPrice.networkGasPrice && gasPrice.maxGasFee) {
          const { delayGasPrice, maxGasFee, setMaxGasFeeForEthMax } = gasPrice
          const affordableMaxValue = getAffordableMaxValue(firstBalance, delayGasPrice, maxGasFee, settings)
          setMaxGasFeeForEthMax(maxGasFee)
          if (affordableMaxValue.lte(0)) {
            alert(INSUFFICIENT_ETH_MSG)
          } else {
            _onFirstChange(new CurrencyValue(firstBalance.currency, affordableMaxValue).toString())
          }
        }
      } else {
        _onFirstChange(firstBalance.toString())
      }
    }
  }

  const price = pair?.getPrice()
  const firstReserve = pair?.getFirstReserve()
  const secondReserve = pair?.getSecondReserve()

  useEffect(() => {
    if (controlled === 'first') {
      if (currencies.first && currencies.second) {
        if (pair?.isEmpty() === false) {
          const firstIn = CurrencyValue.fromString(currencies.first, firstValue)
          setSecondValue(pair?.getSwapAmountOut(firstIn)?.toString() ?? '')
        }
      } else {
        setSecondValue('')
      }
    }
  }, [controlled, firstValue, currencies.first, currencies.second, price, firstReserve, secondReserve])

  useEffect(() => {
    if (controlled === 'second') {
      if (currencies.first && currencies.second) {
        if (pair?.isEmpty() === false) {
          const secondIn = CurrencyValue.fromString(currencies.second, secondValue)
          setFirstValue(pair?.getSwapAmountIn(secondIn)?.toString() ?? '')
        }
      } else {
        setFirstValue('')
      }
    }
  }, [controlled, secondValue, currencies.first, currencies.second, price, firstReserve, secondReserve])

  const [firstCurrencyValue, firstCurrencyValue18] = getCurrencyValues(currencies.first, firstValue)
  const [secondCurrencyValue, secondCurrencyValue18] = getCurrencyValues(currencies.second, secondValue)

  const firstPriceSymbol = currencies.first.isNative() ? 'WETH' : currencies.first.symbol
  const secondPriceSymbol = currencies.second.isNative() ? 'WETH' : currencies.second.symbol
  const firstTokenPrice = apiTokenPrices?.[firstPriceSymbol]
  const secondTokenPrice = apiTokenPrices?.[secondPriceSymbol]
  let firstUsdValue
  let secondUsdValue
  let firstUsdValue18
  let secondUsdValue18
  if (firstTokenPrice && secondTokenPrice) {
    firstUsdValue18 = firstCurrencyValue18.mul(firstTokenPrice.price).div(BN_10_18)
    secondUsdValue18 = secondCurrencyValue18.mul(secondTokenPrice.price).div(BN_10_18)
    const difference = firstUsdValue18.isZero()
      ? BigNumber.from(0)
      : secondUsdValue18.sub(firstUsdValue18).mul(BN_10_18).div(firstUsdValue18)
    firstUsdValue = `~$${formatCurrency(18, firstUsdValue18, 2)}`
    secondUsdValue = `~$${formatCurrency(18, secondUsdValue18, 2)} (${
      difference.gt(BigNumber.from(0)) ? '+' : ''
    }${formatCurrency(3, difference.div(BigNumber.from(10).pow(13)))}%)`
  }

  let firstPrice
  let firstRate
  let secondPrice
  let secondRate
  if (!firstCurrencyValue18.isZero() && !secondCurrencyValue18.isZero()) {
    firstPrice = secondCurrencyValue18.mul(BN_10_18).div(firstCurrencyValue18)
    firstRate = `1 ${firstCurrencyValue.currency.symbol} = ${formatCurrency(18, firstPrice)} ${
      secondCurrencyValue.currency.symbol
    }`
    secondPrice = firstCurrencyValue18.mul(BN_10_18).div(secondCurrencyValue18)
    secondRate = `1 ${secondCurrencyValue.currency.symbol} = ${formatCurrency(18, secondPrice)} ${
      firstCurrencyValue.currency.symbol
    }`
  }

  let swapFee
  let slippagePercent
  if (pair && price && !price.isZero()) {
    const inverted = pair.getToken0Symbol() !== firstCurrencyValue.currency.symbol
    swapFee = new CurrencyValue(currencies.first, firstCurrencyValue.value.mul(pair.getSwapFee()).div(BN_10_18))
    const swapFee18 = firstCurrencyValue18.mul(pair.getSwapFee()).div(BN_10_18)
    const firstCurrentValue18AfterFee = firstCurrencyValue18.sub(swapFee18)
    const spotPriceOutout = inverted
      ? firstCurrentValue18AfterFee.mul(BN_10_18).div(price)
      : firstCurrentValue18AfterFee.mul(price).div(BN_10_18)
    if (!spotPriceOutout.isZero()) {
      const difference = spotPriceOutout.sub(secondCurrencyValue18).mul(BN_10_18).div(spotPriceOutout)
      slippagePercent = formatCurrency(2, difference.div(BigNumber.from(10).pow(14)))
    }
  }

  let state = SwapState.NO_ISSUE
  if (!currencies.first || !currencies.second) {
    state = SwapState.TOKEN_NOT_SELECTED
  } else if (!exists) {
    state = SwapState.PAIR_DOES_NOT_EXIST
  } else if (
    !pair ||
    !firstCurrencyValue ||
    !secondCurrencyValue ||
    (account && (!firstBalance || !secondBalance || !firstApproval || !secondApproval))
  ) {
    state = SwapState.LOADING
  } else if (pair.isEmpty()) {
    state = SwapState.EMPTY
  } else if (secondReserve && secondReserve.lt(secondCurrencyValue)) {
    state = SwapState.INSUFFICIENT_SECOND_RESERVE
  } else if (firstBalance && firstBalance.lt(firstCurrencyValue)) {
    state = SwapState.INSUFFICIENT_FIRST_BALANCE
  } else if (!firstValue || !secondValue) {
    state = SwapState.MISSING_INPUT
  } else if (firstApproval && firstApproval.lt(firstCurrencyValue)) {
    state = SwapState.NEED_FIRST_APPROVAL
  } else {
    const inverted = pair.getToken0Symbol() !== firstCurrencyValue.currency.symbol
    const priceDeviationLimit = pair.getPriceDeviationLimit()
    const secondAbsoluteLimit = pair.getSecondAbsoluteLimit()
    const secondRelativeLimit = pair.getSecondRelativeLimit()

    if (secondAbsoluteLimit && secondCurrencyValue.value.gt(secondAbsoluteLimit.value)) {
      state = inverted ? SwapState.TOKEN0_ABSOLUTE_LIMIT : SwapState.TOKEN1_ABSOLUTE_LIMIT
    } else if (
      secondRelativeLimit &&
      secondReserve &&
      !secondReserve.value.isZero() &&
      secondCurrencyValue.value.mul(BN_10_18).div(secondReserve.value).gt(secondRelativeLimit.value)
    ) {
      state = inverted ? SwapState.TOKEN0_RELATIVE_LIMIT : SwapState.TOKEN1_RELATIVE_LIMIT
    } else if (
      priceDeviationLimit &&
      price &&
      !price.isZero() &&
      firstPrice &&
      !firstPrice.isZero() &&
      secondPrice &&
      !secondPrice.isZero()
    ) {
      const currentPrice = inverted ? secondPrice : firstPrice
      const difference = price.gt(currentPrice) ? price.sub(currentPrice) : currentPrice.sub(price)
      if (difference.mul(BN_10_18).div(price).gt(priceDeviationLimit)) {
        state = SwapState.PRICE_DEVIATION_LIMIT
      }
    }
  }

  const firstToken = toToken(currencies.first)
  const secondToken = toToken(currencies.second)
  const [debouncedFirstCurrencyValue, debouncedSecondCurrencyValue] = useDebouncePair<CurrencyValue, CurrencyValue>(
    firstCurrencyValue,
    secondCurrencyValue,
    750
  )
  const {
    exchangeQuotes,
    timeoutStartAt: exchangeQuotesTimeoutStartAt,
    autoRefresh: exchangeQuotesAutoRefresh,
    toggleAutoRefresh: exchangeQuotesToggleAutoRefresh,
    refresh: exchangeQuotesRefresh,
  } = useComparator(
    firstToken,
    secondToken,
    controlled,
    controlled === 'first' ? debouncedFirstCurrencyValue : debouncedSecondCurrencyValue
  )

  if (!account) {
    if (STATES_TO_SHOW_OUTPUT.includes(state)) {
      state = SwapState.WALLET_NOT_CONNECTED_SHOW_OUTPUT
    } else {
      state = SwapState.WALLET_NOT_CONNECTED_HIDE_OUTPUT
    }
  }

  return {
    currencies,
    firstRate,
    firstValue,
    setFirstValue: onFirstChange,
    firstUsdValue,
    firstUsdValue18,
    secondRate,
    secondValue,
    setSecondValue: onSecondChange,
    secondUsdValue,
    secondUsdValue18,
    swapFee,
    slippagePercent,
    setMax,
    pair,
    exists,
    state,
    controlled,
    showCalculatedValue: STATES_TO_SHOW_OUTPUT.includes(state),
    exchangeQuotes,
    exchangeQuotesTimeoutStartAt,
    exchangeQuotesAutoRefresh,
    exchangeQuotesToggleAutoRefresh,
    exchangeQuotesRefresh,
  }
}
